import React from "react";
import {
  CheckCircle,
  Clock,
  Settings,
  ArrowRight,
  Edit3,
  XCircle,
  CircleCheck,
  CircleCheckBig,
  Lock,
} from "lucide-react";

import { useNavigate } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "../ui/progress";

const STAT_LABELS = {
  epf_enabled: "EPF (Provident Fund)",
  esi_enabled: "ESI (Health Insurance)",
  professional_tax_enabled: "Professional Tax (PT)",
  lwf_enabled: "Labour Welfare Fund (LWF)",
  tds_enabled: "TDS (Tax Deduction)",
};

const ONBOARDING_MAP = {
  "setup.work_location": {
    id: 1,
    title: "Add Organisation Details",
    description: "Set up company information and work locations.",
    navigateTo: "/settings",
    routeState: { from: "onboarding", tab: "org-structure" },
  },

  "setup.salary_components": {
    id: 2,
    title: "Create Core Salary Components",
    description: "Define earnings like Basic & deductions like PF/ESI.",
    navigateTo: "/salary-components",
    routeState: { openCreateDialog: true, fromOnboarding: true },
  },

  "setup.shifts": {
    id: 3,
    title: "Configure Company Shifts",
    description: "Set up shift timings, breaks, and weekly schedules.",
    navigateTo: "/attendance/shifts",
  },

  "setup.statutory_components": {
    id: 4,
    title: "Set up Statutory Components",
    description: "Enable statutory rules for payroll compliance.",
    navigateTo: "/tax-configuration",
  },

  "setup.employee": {
    id: 5,
    title: "Import/Add Employees",
    description: "Populate your workforce data.",
    navigateTo: "/employees",
  },
};

const AdminOnboardingWizard = ({ steps, statutory_status }) => {
  const navigate = useNavigate();

  const mappedSteps = steps
    ? steps
        .map((step) => ({
          ...ONBOARDING_MAP[step.id],
          isCompleted: step.is_completed,
        }))
        .sort((a, b) => a.id - b.id)
    : [];

  // dependency: employee requires salary + shifts
  const organisationDetailsDone = mappedSteps.find(
    (s) => s.id === 1
  )?.isCompleted;
  const salaryComponentsDone = mappedSteps.find((s) => s.id === 3)?.isCompleted;
  const shiftsDone = mappedSteps.find((s) => s.id === 4)?.isCompleted;

  const blockEmployee =
    !organisationDetailsDone || !salaryComponentsDone || !shiftsDone;

  const total = mappedSteps.length;
  const completed = mappedSteps.filter((s) => s.isCompleted).length;
  const progress = (completed / total) * 100;
  const isComplete = completed === total;

  const PRIMARY_BG_LIGHT = "bg-primary/5 dark:bg-primary/20";
  const PRIMARY_BORDER = "border-primary";
  const PRIMARY_TEXT = "text-primary";
  const PRIMARY_BUTTON_CLASS =
    "bg-primary hover:bg-primary/90 text-primary-foreground";

  const COMPLETED_BG_LIGHT = "bg-gray-100 dark:bg-gray-800";
  const COMPLETED_BORDER = "border-gray-300 dark:border-gray-700";
  const COMPLETED_TEXT_FADE = "text-gray-500 dark:text-gray-400";
  const COMPLETED_ICON_BG = "bg-primary/80";

  const AMBER_BG = "bg-amber-500";

  const HEADER_INCOMPLETE_BG = "bg-gray-50 dark:bg-gray-900";
  const HEADER_BG = isComplete ? COMPLETED_BG_LIGHT : HEADER_INCOMPLETE_BG;
  const HEADER_ICON_COLOR = isComplete ? COMPLETED_TEXT_FADE : PRIMARY_TEXT;

  return (
    <div className="flex justify-center">
      <Card className="w-full max-w-4xl shadow-xl">
        <CardHeader
          className={`
            p-6 sm:p-8 
            rounded-t-xl 
            border-b-4 
            ${HEADER_BG}
            ${isComplete ? "border-gray-200 dark:border-gray-700" : "border-primary/50"} 
            ${!isComplete ? "shadow-md" : ""} 
          `}
        >
          <CardTitle className="flex items-center gap-4 text-2xl font-bold">
            <Settings className={HEADER_ICON_COLOR} size={36} />
            <span className={`${isComplete ? "text-gray-800 dark:text-gray-200" : "text-gray-900 dark:text-gray-100" }`}>
              {isComplete
                ? "Setup Complete 🎉"
                : "Welcome! Start Your Platform Setup"}
            </span>
          </CardTitle>
          <p className="text-base mt-1 text-gray-600 dark:text-gray-400">
            {isComplete
              ? "All core configuration steps are finalized. You are ready for full operation!"
              : "Follow these 6 essential configuration steps to unlock your full platform features and ensure compliance."}
          </p>
        </CardHeader>

        <CardContent>
          {/* Progress */}
          <div className="my-8">
            <div className="flex justify-between text-sm mb-2 font-medium text-gray-700 dark:text-gray-300">
              <span>
                <b>{completed}</b> of <b>{total}</b> steps completed
              </span>
              <span className={PRIMARY_TEXT}>{progress.toFixed(0)}%</span>
            </div>
            <Progress value={progress} />
          </div>

          {/* Steps */}
          <div className="space-y-4">
            {mappedSteps.map((step) => {
              const isEmployeeStep = step.id === 6;
              const isBlocked = isEmployeeStep && blockEmployee;

              const stepClasses = step.isCompleted
                ? `${COMPLETED_BG_LIGHT} border ${COMPLETED_BORDER}`
                : isBlocked
                ? "bg-gray-100 dark:bg-gray-800 border border-gray-300 opacity-60 cursor-not-allowed"
                : "bg-gray-50 dark:bg-gray-800 border border-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer transform hover:scale-[1.01]";

              return (
                <div
                  key={step.id}
                  className={`p-5 rounded-xl transition-all ${stepClasses}`}
                  onClick={() => {
                    if (isBlocked) return;
                    if (!step.isCompleted)
                      navigate(step.navigateTo, {
                        state: step.routeState || {},
                      });
                  }}
                >
                  <div className="flex justify-between items-center">
                    {/* LEFT */}
                    <div className="flex items-center gap-4">
                      <div
                        className={`p-3 rounded-full ${
                          step.isCompleted ? COMPLETED_ICON_BG : AMBER_BG
                        }`}
                      >
                        {step.isCompleted ? (
                          <CheckCircle className="text-white w-5 h-5" />
                        ) : (
                          <Clock className="text-white w-5 h-5" />
                        )}
                      </div>

                      <div>
                        <h3
                          className={`font-extrabold text-lg ${
                            step.isCompleted ? COMPLETED_TEXT_FADE : ""
                          }`}
                        >
                          {step.title}
                        </h3>

                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {step.description}
                        </p>

                        {isBlocked && (
                          <p className="text-xs mt-2 text-red-600 dark:text-red-400">
                            Complete Organisation Details, Salary Components and
                            Company Shifts before adding employees.
                          </p>
                        )}
                      </div>
                    </div>

                    {/* RIGHT BUTTON */}
                    <Button
                      disabled={isBlocked}
                      variant={step.isCompleted ? "outline" : "default"}
                      className={`
    group flex items-center gap-2 px-4 py-2 rounded-lg transition-all

    ${
      isBlocked
        ? "opacity-60 cursor-not-allowed bg-gray-200 dark:bg-gray-700 text-gray-500"
        : ""
    }
    ${
      !step.isCompleted && !isBlocked
        ? "bg-primary text-white hover:bg-primary/90 shadow-md hover:shadow-lg"
        : ""
    }
    ${
      step.isCompleted && !isBlocked
        ? "border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
        : ""
    }
  `}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!isBlocked)
                          navigate(step.navigateTo, {
                            state: step.routeState || {},
                          });
                      }}
                    >
                      {/* ICONS */}
                      {step.isCompleted ? (
                        <CircleCheckBig className="w-4 h-4 text-emerald-600" />
                      ) : isBlocked ? (
                        <Lock className="w-4 h-4 text-gray-500" />
                      ) : (
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      )}

                      {/* LABEL */}
                      <span className="text-sm font-medium">
                        {step.isCompleted
                          ? "Completed"
                          : isBlocked
                          ? "Locked"
                          : "Start Setup"}
                      </span>
                    </Button>
                  </div>

                  {/* Statutory Chips */}
                  {step.id === 4 && statutory_status && (
                    <div className="mt-3 flex flex-wrap gap-3">
                      {Object.keys(statutory_status).map((key) => {
                        const Enabled = statutory_status[key];
                        return (
                          <div
                            key={key}
                            className={`px-2 py-1 rounded-full flex items-center gap-1.5 text-xs font-medium
                              ${
                                Enabled
                                  ? "bg-green-50 border border-green-300 text-green-700"
                                  : "bg-red-50 border border-red-300 text-red-700"
                              }`}
                          >
                            {Enabled ? (
                              <CheckCircle size={12} />
                            ) : (
                              <XCircle size={12} />
                            )}
                            <span>{STAT_LABELS[key]}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {isComplete && (
            <div className="mt-8 text-center p-4 bg-gray-100 dark:bg-gray-700 rounded-lg">
              <p className="text-lg font-semibold text-gray-700 dark:text-gray-200">
                You're all set! Ready to process your first payroll.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminOnboardingWizard;
